import { Component, forwardRef, Input } from '@angular/core';
import {
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  Validator,
} from '@angular/forms';

@Component({
  selector: 'app-verification-code-input',
  templateUrl: './verification-code-input.component.html',
  styleUrls: ['./verification-code-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => VerificationCodeInputComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => VerificationCodeInputComponent),
      multi: true,
    },
  ],
})
export class VerificationCodeInputComponent
  implements ControlValueAccessor, Validator
{
  form: FormGroup;

  @Input() codeLength = 6;
  disabled = false;

  constructor(private fb: FormBuilder) {
    this.initForm();
  }

  // Public Methods
  // ------------------------------------------------------------------------------------

  get value(): string[] {
    return Object.values(this.form.value);
  }

  onFocus(ref) {
    ref.select();
    this.onTouched();
  }

  onPaste(event: ClipboardEvent) {
    const pastedText = event.clipboardData.getData('text');
    const regex = new RegExp(`^\\d{${this.codeLength}}$`);

    if (regex.test(pastedText)) {
      const digitArr = pastedText.split('');
      digitArr.forEach((digit, i) => {
        this.form.controls['input' + i].setValue(digit);
      });
    }
    return false;
  }

  onKeyup(event: KeyboardEvent, index) {
    const currInput = this.form.controls['input' + index];
    const key = event.key;

    switch (key) {
      case 'ArrowLeft':
      case 'Backspace':
        this.focusTo(index - 1);
        break;
      case 'ArrowRight':
        this.focusTo(index + 1);
        break;
      case '0':
      case '1':
      case '2':
      case '3':
      case '4':
      case '5':
      case '6':
      case '7':
      case '8':
      case '9':
        if (key === currInput.value) {
          this.focusTo(index + 1);
        }
        break;
    }
  }

  // ControlValueAccessor Methods
  // ------------------------------------------------------------------------------------

  onChange = (code: string[]) => {};
  onTouched = () => {};

  writeValue(code: string[]): void {
    if (code === null) {
      for (let i = 0; i < this.codeLength; i++) {
        this.form.controls['input' + i].setValue('', { emitEvent: false });
      }
    } else {
      for (let i = 0; i < Math.min(code.length, this.codeLength); i++) {
        this.form.controls['input' + i].setValue(code[i], { emitEvent: false });
      }
    }
    this.onChange(
      this.isValid(this.value)
        ? this.value
        : new Array(this.codeLength).fill('')
    );
  }

  registerOnChange(fn: (code: string[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // Validator Methods
  // ------------------------------------------------------------------------------------

  validate(c: FormControl) {
    return this.isValid(c.value) ? null : { invalid: true };
  }

  // Private Methods
  // ------------------------------------------------------------------------------------

  private initForm(): void {
    this.form = this.fb.group({});
    for (let i = 0; i < this.codeLength; i++) {
      this.form.addControl('input' + i, this.fb.control(''));
    }
    this.form.valueChanges.subscribe((formValue) => {
      this.writeValue(Object.values(formValue));
    });
  }

  // focus input by element id
  private focusTo(index): void {
    const ele = document.getElementById('input' + index);
    if (ele) {
      ele.focus();
    }
  }

  private isValid(codeArr): boolean {
    const regex = new RegExp(`^\\d{${this.codeLength}}$`);
    return codeArr && Array.isArray(codeArr) && regex.test(codeArr.join(''));
  }
}
